<script setup>
import { useAlert } from 'dashboard/composables';
import CustomRoleAPI from 'dashboard/api/customRole';
import CustomRoleModal from './component/CustomRoleModal.vue';
import CustomRoleTableBody from './component/CustomRoleTableBody.vue';
import CustomRolePaywall from './component/CustomRolePaywall.vue';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

const store = useStore();
const { t } = useI18n();

const showCustomRoleModal = ref(false);
const customRoleModalMode = ref('add');
const selectedRole = ref(null);
const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const showDefaultPresetsModal = ref(false);
const isLoadingDefaultPresets = ref(false);
const defaultRolePresets = ref([]);
const activeResponse = ref({});
const searchQuery = ref('');

const records = useMapGetter('customRole/getCustomRoles');
const uiFlags = useMapGetter('customRole/getUIFlags');

// Filtered roles based on search
const filteredRecords = computed(() => {
  if (!searchQuery.value) return records.value;
  const query = searchQuery.value.toLowerCase();
  return records.value.filter(
    role =>
      role.name.toLowerCase().includes(query) ||
      (role.description && role.description.toLowerCase().includes(query))
  );
});

const deleteConfirmText = computed(
  () => `${t('CUSTOM_ROLE.DELETE.CONFIRM.YES')} ${activeResponse.value.name}`
);

const deleteRejectText = computed(
  () => `${t('CUSTOM_ROLE.DELETE.CONFIRM.NO')} ${activeResponse.value.name}`
);

const deleteMessage = computed(() => {
  return ` ${activeResponse.value.name} ? `;
});

const isFeatureEnabledOnAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const currentAccountId = useMapGetter('getCurrentAccountId');

const isBehindAPaywall = computed(() => {
  return !isFeatureEnabledOnAccount.value(
    currentAccountId.value,
    'custom_roles'
  );
});

const fetchCustomRoles = async () => {
  try {
    await store.dispatch('customRole/getCustomRole');
  } catch (error) {
    // Ignore Error
  }
};

onMounted(() => {
  fetchCustomRoles();
});

const tableHeaders = computed(() => {
  return [
    t('CUSTOM_ROLE.LIST.TABLE_HEADER.NAME'),
    t('CUSTOM_ROLE.LIST.TABLE_HEADER.DESCRIPTION'),
    t('CUSTOM_ROLE.LIST.TABLE_HEADER.PERMISSIONS'),
  ];
});

const showAlertMessage = message => {
  loading.value[activeResponse.value.id] = false;
  activeResponse.value = {};
  useAlert(message);
};

const openAddModal = () => {
  if (isBehindAPaywall.value) return;
  customRoleModalMode.value = 'add';
  selectedRole.value = null;
  showCustomRoleModal.value = true;
};

const openEditModal = role => {
  customRoleModalMode.value = 'edit';
  selectedRole.value = role;
  showCustomRoleModal.value = true;
};

const hideCustomRoleModal = () => {
  selectedRole.value = null;
  showCustomRoleModal.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  activeResponse.value = response;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const getPermissionLabel = permission =>
  t(`CUSTOM_ROLE.PERMISSIONS.${permission.toUpperCase()}`);

const openDefaultPresetsModal = async () => {
  if (isBehindAPaywall.value) return;
  isLoadingDefaultPresets.value = true;
  try {
    const response = await CustomRoleAPI.getDefaultPresets();
    defaultRolePresets.value = response.data.presets || [];
    showDefaultPresetsModal.value = true;
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    isLoadingDefaultPresets.value = false;
  }
};

const closeDefaultPresetsModal = () => {
  showDefaultPresetsModal.value = false;
};

const deleteCustomRole = async id => {
  try {
    await store.dispatch('customRole/deleteCustomRole', id);
    showAlertMessage(t('CUSTOM_ROLE.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('CUSTOM_ROLE.DELETE.API.ERROR_MESSAGE');
    showAlertMessage(errorMessage);
  }
};

const confirmDeletion = () => {
  loading[activeResponse.value.id] = true;
  closeDeletePopup();
  deleteCustomRole(activeResponse.value.id);
};

const installDefaultRoles = async () => {
  closeDefaultPresetsModal();
  try {
    const response = await store.dispatch('customRole/installDefaultRoles');
    useAlert(
      t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.API.SUCCESS_MESSAGE', {
        created: response.created_count,
        skipped: response.skipped_count,
      })
    );
  } catch (error) {
    const errorMessage =
      error?.message ||
      t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="settings-header flex items-center justify-between px-6 border-b flex-shrink-0"
      style="height: 50px; background: #ffffff; border-bottom-color: #e5e7eb"
    >
      <h1 class="text-xl font-semibold" style="color: #111827">
        {{ $t('CUSTOM_ROLE.HEADER') }}
      </h1>
      <div class="flex items-center gap-3">
        <button
          class="secondary-action-btn"
          :disabled="
            isBehindAPaywall ||
            uiFlags.installingDefaults ||
            isLoadingDefaultPresets
          "
          @click="openDefaultPresetsModal"
        >
          <span class="i-ph-lightning-bold size-4" />
          {{ $t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.BUTTON_TEXT') }}
        </button>
        <button
          class="primary-action-btn"
          :disabled="isBehindAPaywall"
          @click="openAddModal"
        >
          <span class="i-ph-plus-bold size-4" />
          {{ $t('CUSTOM_ROLE.HEADER_BTN_TXT') }}
        </button>
      </div>
    </header>

    <!-- Description -->
    <div
      class="px-6 py-3 border-b"
      style="border-bottom-color: #e5e7eb; background: #ffffff"
    >
      <p class="text-sm" style="color: #4b5563">
        {{ $t('CUSTOM_ROLE.DESCRIPTION') }}
      </p>
      <p v-if="!isBehindAPaywall" class="mt-2 text-xs" style="color: #6b7280">
        {{ $t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.HELP_TEXT') }}
      </p>
    </div>

    <!-- Paywall -->
    <CustomRolePaywall v-if="isBehindAPaywall" class="flex-1" />

    <template v-else>
      <!-- Search and Filters -->
      <div
        class="px-6 py-3 border-b"
        style="border-bottom-color: #e5e7eb; background: #ffffff"
      >
        <div class="flex items-center gap-4">
          <div class="relative flex-1 max-w-xs">
            <span
              class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
              style="color: #9ca3af"
            />
            <input
              v-model="searchQuery"
              type="text"
              class="search-input reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
              placeholder="Search roles..."
            />
          </div>
          <div class="text-sm self-center" style="color: #6b7280">
            {{ filteredRecords.length }} roles
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6" style="background: #ffffff">
        <!-- Loading State -->
        <div
          v-if="uiFlags.fetchingList"
          class="flex items-center justify-center py-20"
        >
          <woot-loading-state :message="$t('CUSTOM_ROLE.LOADING')" />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!filteredRecords.length"
          class="flex items-center justify-center py-20"
        >
          <p style="color: #6b7280">{{ $t('CUSTOM_ROLE.LIST.404') }}</p>
        </div>

        <!-- Custom Roles Table -->
        <div v-else class="cr-table-wrap">
          <table class="cr-table w-full">
            <thead>
              <tr class="cr-table-head">
                <th
                  v-for="thHeader in tableHeaders"
                  :key="thHeader"
                  class="cr-th px-6 py-3 text-left"
                >
                  {{ thHeader }}
                </th>
                <th class="cr-th px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <CustomRoleTableBody
              :roles="filteredRecords"
              :loading="loading"
              @edit="openEditModal"
              @delete="openDeletePopup"
            />
          </table>
        </div>
      </div>
    </template>

    <!-- Modals -->
    <woot-modal
      v-model:show="showCustomRoleModal"
      :on-close="hideCustomRoleModal"
      size="medium"
    >
      <CustomRoleModal
        :mode="customRoleModalMode"
        :selected-role="selectedRole"
        @close="hideCustomRoleModal"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CUSTOM_ROLE.DELETE.CONFIRM.TITLE')"
      :message="$t('CUSTOM_ROLE.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-modal
      v-model:show="showDefaultPresetsModal"
      :on-close="closeDefaultPresetsModal"
      size="medium"
    >
      <div class="default-presets-modal">
        <woot-modal-header
          :header-title="$t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.MODAL.TITLE')"
          :header-content="
            $t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.MODAL.DESCRIPTION')
          "
        />
        <div class="default-presets-body">
          <div
            v-for="preset in defaultRolePresets"
            :key="preset.name"
            class="preset-card"
          >
            <div class="flex items-start justify-between gap-4">
              <div>
                <h4 class="preset-title">{{ preset.name }}</h4>
                <p class="preset-description">{{ preset.description }}</p>
              </div>
              <span class="preset-count"
                >{{ preset.permission_count }}
                {{
                  $t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.MODAL.PERMISSIONS')
                }}</span
              >
            </div>
            <div class="preset-permissions">
              <span
                v-for="permission in preset.permissions"
                :key="`${preset.name}-${permission}`"
                class="preset-permission-chip"
              >
                {{ getPermissionLabel(permission) }}
              </span>
            </div>
          </div>
        </div>
        <div class="default-presets-footer">
          <button
            class="secondary-action-btn"
            @click="closeDefaultPresetsModal"
          >
            {{ $t('CUSTOM_ROLE.FORM.CANCEL_BUTTON_TEXT') }}
          </button>
          <button
            class="primary-action-btn"
            :disabled="uiFlags.installingDefaults"
            @click="installDefaultRoles"
          >
            {{ $t('CUSTOM_ROLE.DEFAULT_ACCESS_PRESETS.MODAL.CONFIRM_BUTTON') }}
          </button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped>
/* Force white theme */
.settings-page,
.dark .settings-page {
  background-color: #ffffff !important;
}

.settings-header {
  background-color: #ffffff !important;
  border-bottom-color: #e5e7eb !important;
}

.search-input {
  background-color: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}

.search-input:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
}

.search-input::placeholder {
  color: #9ca3af !important;
}

/* Table */
.cr-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}

.cr-table {
  border-collapse: collapse;
  width: 100%;
}

.cr-table-head {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

/* Force header cells readable on white */
.cr-th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb !important;
}

/* Override token-based text/bg in sub-components */
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
:deep(tr:hover) {
  background: #f9fafb !important;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family:
    'Excon',
    system-ui,
    -apple-system,
    sans-serif;
  background-color: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.primary-action-btn:hover {
  background-color: #1f2937;
}

.primary-action-btn:active {
  transform: scale(0.98);
}

.primary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.secondary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family:
    'Excon',
    system-ui,
    -apple-system,
    sans-serif;
  background-color: #ffffff;
  color: #111827;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.secondary-action-btn:hover {
  background-color: #f9fafb;
}

.secondary-action-btn:active {
  transform: scale(0.98);
}

.secondary-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.default-presets-modal {
  background: #ffffff;
}

:deep(.default-presets-modal .modal-header) {
  background: #ffffff !important;
}

.default-presets-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.preset-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  background: #ffffff;
}

.preset-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
}

.preset-description {
  margin-top: 0.25rem;
  font-size: 0.85rem;
  color: #4b5563;
}

.preset-count {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280;
}

.preset-permissions {
  margin-top: 0.75rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.preset-permission-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.55rem;
  border-radius: 9999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 0.75rem;
  line-height: 1.2;
}

.default-presets-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 0 1.5rem 1.25rem;
}

/* Ensure add/edit custom role modal always uses light surface */
:deep(.modal-mask) {
  background: rgba(15, 23, 42, 0.45) !important;
  backdrop-filter: blur(4px);
}

:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}

:deep(.modal-mask .modal-container .modal-header),
:deep(.modal-mask .modal-container .content),
:deep(.modal-mask .modal-container form) {
  background: #ffffff !important;
}

:deep(.modal-mask .modal-container label) {
  color: #111827 !important;
}

:deep(.modal-mask .modal-container input:not(.reset-base)),
:deep(.modal-mask .modal-container textarea:not(.reset-base)),
:deep(.modal-mask .modal-container select:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
</style>
